/**
 * Serviço de Envio para APIs de Produção e Temp
 *
 * Origem: linhas 1077-1409 do useSaveController.ts
 *
 * CONSOLIDAÇÃO CRÍTICA: Tratamento 409 (duplicação 3/4 eliminada)
 */

import { api, APIError } from "../../../lib/api";
import { sendToTerminal, terminalLog } from "../../../lib/terminalLogger";
import { handleApiError } from "../../../lib/errors";
import { storage } from "../../../lib/storage/storage";
import { Keys, readWithAliases } from "../../../lib/storage/keys";
import { validateApiPayload } from "../utils/payloadValidators";
import { generateIdempotencyKey } from "../utils/idempotencyKeyGenerator";
import { detectSaveFlow, isTempFlow } from "../utils/flowDetector";
import { ensureCaminhoArquivo } from "../utils/saveDataTransformers";
import {
	ERROR_INSUFFICIENT_DATA,
	ERROR_OPERATION_CANCELLED,
	IDEM_KEY_TYPE_SAVE,
	IDEM_KEY_TYPE_TEMP,
} from "../constants/saveConstants";
import { API_URL } from "../../../config";

/**
 * Opções para envio de produção
 */
export interface ProductionSaveOptions {
	/**
	 * Payload da API gerado
	 */
	payload: any;

	/**
	 * Idempotency key (ref para manter consistência)
	 */
	idemKeyRef: React.MutableRefObject<string | null>;

	/**
	 * Hash do arquivo para idempotência
	 */
	fileHash?: string;

	/**
	 * Função de erro
	 */
	setErrorMessage: (msg: string) => void;

	/**
	 * Função para mostrar diálogo de confirmação
	 */
	showConfirmDialog: () => Promise<boolean>;
}

/**
 * Opções para envio temp
 */
export interface TempSaveOptions {
	/**
	 * Dados de salvamento
	 */
	saveData: any;

	/**
	 * Referência de dados
	 */
	saveDataRef: React.MutableRefObject<any>;

	/**
	 * Caminho do arquivo salvo
	 */
	savedFilePathRef: React.MutableRefObject<string>;

	/**
	 * Último metadado salvo
	 */
	lastSaveMetaRef: React.MutableRefObject<any>;

	/**
	 * Role do usuário
	 */
	role?: string;

	/**
	 * Idempotency key (ref)
	 */
	idemKeyRef: React.MutableRefObject<string | null>;

	/**
	 * Função de erro
	 */
	setErrorMessage: (msg: string) => void;

	/**
	 * Função para mostrar diálogo de confirmação
	 */
	showConfirmDialog: () => Promise<boolean>;
}

/**
 * Opções para wrapper sendOnce
 */
export interface SendOnceOptions extends TempSaveOptions {
	/**
	 * Guard para evitar múltiplos envios
	 */
	apiSentRef: React.MutableRefObject<boolean>;

	/**
	 * Função para envio de produção (fallback)
	 */
	sendToProduction: () => Promise<boolean>;
}

/**
 * Serviço de envio para APIs de Produção e Temp
 *
 * ELIMINAÇÃO DA DUPLICAÇÃO 3/4:
 * - Tratamento 409 consolidado em handleConflict409()
 * - Suporta tanto produção quanto temp
 * - Mantém mesma Idempotency-Key em retry
 */
export class ApiSaveService {
	/**
	 * Envia para API de produção (/cdr/arquivos/salvar)
	 *
	 * Validações:
	 * - Payload obrigatório
	 * - Campos críticos (orc_id, oit_id, pro_id, etc)
	 * - Idempotency key baseada em hash do arquivo
	 *
	 * @param options - Opções de envio
	 * @returns true se sucesso, false se erro
	 */
	static async sendToProduction(
		options: ProductionSaveOptions,
	): Promise<boolean> {
		const {
			payload,
			idemKeyRef,
			fileHash,
			setErrorMessage,
			showConfirmDialog,
		} = options;

		try {
			// 1. VALIDAÇÃO DO PAYLOAD
			if (!payload) {
				setErrorMessage(ERROR_INSUFFICIENT_DATA);
				return false;
			}

			const validation = validateApiPayload(payload);
			if (!validation.ok) {
				sendToTerminal("❌ [SAVE:API] Validação falhou", {
					missing: validation.missing,
					url: `${API_URL}/cdr/arquivos/salvar`,
				});
				setErrorMessage(
					"Dados insuficientes para salvar. Campos ausentes: " +
						validation.missing.join(", "),
				);
				return false;
			}

			// 2. GERAR IDEMPOTENCY KEY (apenas uma vez)
			if (!idemKeyRef.current) {
				idemKeyRef.current = generateIdempotencyKey({
					type: IDEM_KEY_TYPE_SAVE,
					orcId: payload.orc_id,
					oitId: payload.oit_id,
					hash: fileHash,
				});
			}

			const headers = { "Idempotency-Key": idemKeyRef.current } as Record<
				string,
				string
			>;

			// 3. LOG PRÉ-ENVIO
			sendToTerminal("📤 [DEBUG:API_PAYLOAD] Enviando para API", {
				url: "/cdr/arquivos/salvar",
				hasPayloadSnapshot: !!payload.payload_snapshot,
				payloadSnapshotType: typeof payload.payload_snapshot,
				isPayloadSnapshotEmpty: payload.payload_snapshot
					? Object.keys(payload.payload_snapshot).length === 0
					: true,
				objetosCount: payload.objetos?.length || 0,
				fields: Object.keys(payload),
				idempotencyKey: idemKeyRef.current,
			});

			terminalLog.debug("API_SAVE:REQUEST", {
				url: `${API_URL}/cdr/arquivos/salvar`,
				method: "POST",
				payload: { ...payload, caminho_arquivo: "***" },
				headers,
			});

			// 4. ENVIAR PARA API
			const data: any = await api("/cdr/arquivos/salvar", {
				method: "POST",
				headers,
				body: JSON.stringify(payload),
			});

			// 5. VALIDAR RESPOSTA
			if (data?.ok === true || data?.success === true) {
				return true;
			}

			// 6. ERRO NA RESPOSTA
			sendToTerminal("❌ [SAVE:API] Erro ao registrar arquivo", {
				error: data?.error?.message || data?.message || "Erro desconhecido",
				status: data?.status,
			});
			setErrorMessage(
				`Erro ao registrar arquivo: ${
					data?.error?.message || data?.message || "Erro desconhecido"
				}`,
			);
			return false;
		} catch (e: any) {
			sendToTerminal("❌ [SAVE:API] Exceção ao salvar na API", {
				kind: e instanceof APIError ? "APIError" : typeof e,
				status: e?.status,
				message: e?.message,
			});

			// 7. TRATAMENTO ESPECIAL PARA 409 (CONSOLIDADO)
			if (
				e instanceof APIError &&
				e.status === 409 &&
				(e.data?.code === "WAW_CONFLICT" ||
					e.data?.error?.requires_confirmation ||
					e.data?.requires_confirmation)
			) {
				return await this.handleConflict409Production(
					payload,
					idemKeyRef.current!,
					setErrorMessage,
					showConfirmDialog,
				);
			}

			// 8. OUTROS ERROS
			if (e instanceof APIError) {
				const appError = handleApiError(e);
				setErrorMessage(appError.message);
				return false;
			}

			setErrorMessage(e?.message || "Erro ao se comunicar com o servidor");
			return false;
		}
	}

	/**
	 * Envia para API de orçamentos temp (/cdr/orcamentos-temp/salvamento)
	 *
	 * Usado para pedidos sem pro_id ou fluxo temp explícito
	 *
	 * @param options - Opções de envio
	 * @returns true se sucesso, false se erro
	 */
	static async sendToTemp(options: TempSaveOptions): Promise<boolean> {
		const {
			saveData,
			saveDataRef,
			savedFilePathRef,
			lastSaveMetaRef,
			role,
			idemKeyRef,
			setErrorMessage,
			showConfirmDialog,
		} = options;

		try {
			// 1. PREPARAR DADOS
			let working = saveDataRef.current || saveData || {};

			try {
				working = ensureCaminhoArquivo(working);
			} catch {
				// Continuar mesmo se ensureCaminhoArquivo falhar
			}

			const caminho = String(
				savedFilePathRef.current || working?.caminho_arquivo || "",
			);
			const nome_arquivo = caminho
				? caminho.split("\\").pop() || ""
				: working?.nome_arquivo || "";

			// 2. DERIVAR DIRETÓRIO COM BARRA FINAL
			let diretorio = "";
			if (caminho && caminho.includes("\\")) {
				diretorio = caminho.substring(0, caminho.lastIndexOf("\\"));
			} else if (working?.diretorios?.interno) {
				diretorio = working.diretorios.interno;
			}
			if (diretorio && !diretorio.endsWith("\\")) {
				diretorio = `${diretorio}\\`;
			}

			// 3. DERIVAR CATEGORIA_DIR (última pasta)
			const dirNoSlash = diretorio.replace(/\\$/, "");
			const categoria_dir = dirNoSlash
				? dirNoSlash.split("\\").pop() || ""
				: "";

			// 4. DADOS BÁSICOS
			const entrega = String(
				working?.configuracoes?.entrega || "HOJE",
			).toUpperCase();
			const usuario = String(working?.usuario || role || "usuario");
			const pedido_id = Number(working?.pedido?.orc_id || 0);
			const item_id = Number(working?.pedido?.oit_id || 0);
			const objetos = Array.isArray(working?.objetos)
				? working.objetos.length
				: 0;

			// 5. INTEGRIDADE
			const integridade =
				lastSaveMetaRef.current && lastSaveMetaRef.current.integridade
					? {
							objetosVerificados:
								lastSaveMetaRef.current.integridade.objetosVerificados ?? 0,
							metadadosOk: lastSaveMetaRef.current.integridade.metadadosOk ?? 0,
							metadadosPerdidos:
								lastSaveMetaRef.current.integridade.metadadosPerdidos ?? 0,
						}
					: undefined;

			// 6. REQUEST_ID (do storage)
			const request_id = (() => {
				try {
					return (
						readWithAliases<string>(
							Keys.export.lastSaveRequestId,
							["lastSaveRequestId"],
							{ area: "session", persistIfAliased: true },
						) || undefined
					);
				} catch {
					return undefined;
				}
			})();

			// 7. IDEMPOTENCY KEY
			if (!idemKeyRef.current) {
				idemKeyRef.current = generateIdempotencyKey({
					type: IDEM_KEY_TYPE_TEMP,
					orcId: pedido_id,
					oitId: item_id,
				});
			}

			// 8. CONSTRUIR PAYLOAD
			const headers = { "X-Idempotency-Key": idemKeyRef.current } as Record<
				string,
				string
			>;
			const body = {
				fluxo: "temp",
				pedido_id,
				item_id,
				caminho,
				diretorio,
				nome_arquivo,
				categoria_dir,
				produto: working?.produto?.nome || categoria_dir || undefined,
				entrega,
				usuario,
				objetos,
				integridade,
				request_id,
				idempotency_key: idemKeyRef.current || undefined,
			};

			// 9. LOG PRÉ-ENVIO
			sendToTerminal("SAVE_TEMP:API_REQUEST", {
				url: `${API_URL}/cdr/orcamentos-temp/salvamento`,
				headers,
				preview: { ...body, caminho: "***", diretorio: "***" },
				lengths: { caminho: caminho.length, diretorio: diretorio.length },
			});

			// 10. ENVIAR PARA API
			const data: any = await api("/cdr/orcamentos-temp/salvamento", {
				method: "POST",
				headers,
				body: JSON.stringify(body),
			});

			// 11. VALIDAR RESPOSTA
			const ok = !!(
				data?.success === true ||
				data?.ok === true ||
				data?.data?.id
			);

			if (!ok) {
				sendToTerminal("SAVE_TEMP:API_RESPONSE_NOT_SUCCESS", { data });
				setErrorMessage(
					`Erro ao registrar Save Temp: ${
						data?.error?.message || data?.message || "Erro desconhecido"
					}`,
				);
				return false;
			}

			sendToTerminal("SAVE_TEMP:API_SUCCESS", {
				id: data?.data?.id,
				created_at: data?.data?.created_at,
			});

			return true;
		} catch (e: any) {
			sendToTerminal("SAVE_TEMP:API_ERROR", {
				kind: e instanceof APIError ? "APIError" : typeof e,
				status: e?.status,
				message: e?.message,
				data: e?.data,
			});

			// 12. TRATAMENTO ESPECIAL PARA 409 (CONSOLIDADO)
			if (
				e instanceof APIError &&
				e.status === 409 &&
				(e?.data?.error?.code === "PEDIDO_JA_SALVO" ||
					e?.data?.code === "PEDIDO_JA_SALVO")
			) {
				const latest = e?.data?.error?.data?.latest || null;
				sendToTerminal("SAVE_TEMP:API_CONFLICT", {
					latest,
					total: e?.data?.error?.data?.total,
				});

				return await this.handleConflict409Temp(
					options,
					idemKeyRef.current!,
					setErrorMessage,
					showConfirmDialog,
				);
			}

			// 13. OUTROS ERROS
			if (e instanceof APIError) {
				const appError = handleApiError(e);
				setErrorMessage(appError.message);

				// Notificação sticky para erro crítico de salvamento
				const { notify } = await import("../../../lib/globalNotify");
				notify({
					type: "error",
					title: "Erro ao Salvar",
					message:
						"Erro ao salvar arquivo. Verifique sua conexão e tente novamente.",
					sticky: true,
					actions: [
						{
							label: "Tentar Novamente",
							onClick: async () => {
								// Retry será tratado pelo componente chamador
							},
						},
					],
				});

				return false;
			}

			setErrorMessage(e?.message || "Erro ao enviar Save Temp para API");

			// Notificação para erro genérico
			const { notify } = await import("../../../lib/globalNotify");
			notify({
				type: "error",
				title: "Erro ao Salvar",
				message:
					"Erro ao salvar arquivo. Verifique sua conexão e tente novamente.",
				sticky: true,
			});

			return false;
		}
	}

	/**
	 * Wrapper com guard para evitar múltiplos envios
	 *
	 * Detecta automaticamente o fluxo (TEMP vs NORMAL) e chama o método adequado
	 *
	 * @param options - Opções de envio
	 * @returns true se sucesso, false se erro
	 */
	static async sendOnce(options: SendOnceOptions): Promise<boolean> {
		const { apiSentRef, saveData, saveDataRef, sendToProduction } = options;

		// 1. GUARD: Se já enviou, retornar imediatamente
		if (apiSentRef.current) {
			return true;
		}

		// 2. VERIFICAR RACE CONDITION
		if (apiSentRef.current) {
			return true;
		}

		// 3. LATCH ANTECIPADO
		apiSentRef.current = true;

		try {
			// 4. DETECTAR FLUXO
			const workingData = saveDataRef.current || saveData;
			const flowInfo = detectSaveFlow(workingData);
			const isTemp = isTempFlow(workingData);

			// Detecção de fluxo - silencioso

			// 5. ENVIAR CONFORME FLUXO
			if (isTemp) {
				const success = await this.sendToTemp(options);
				apiSentRef.current = success;
				return success;
			}

			// 6. FALLBACK: Produção normal
			const success = await sendToProduction();
			apiSentRef.current = success;
			return success;
		} catch (error) {
			apiSentRef.current = false;
			throw error;
		}
	}

	/**
	 * CONSOLIDAÇÃO CRÍTICA: Tratamento de conflito 409 para PRODUÇÃO
	 *
	 * Elimina duplicação das linhas 1158-1172 do original
	 *
	 * Fluxo:
	 * 1. Mostrar diálogo de confirmação
	 * 2. Se confirmado: reenviar com confirmar_update: true
	 * 3. Manter mesma Idempotency-Key
	 *
	 * @param payload - Payload original
	 * @param idempotencyKey - Idempotency key a manter
	 * @param setErrorMessage - Função de erro
	 * @param showConfirmDialog - Função de diálogo
	 * @returns true se sucesso, false se erro
	 */
	private static async handleConflict409Production(
		payload: any,
		idempotencyKey: string,
		setErrorMessage: (msg: string) => void,
		showConfirmDialog: () => Promise<boolean>,
	): Promise<boolean> {
		try {
			// 1. MOSTRAR DIÁLOGO
			const confirmar = await showConfirmDialog();
			if (!confirmar) {
				setErrorMessage(ERROR_OPERATION_CANCELLED);
				return false;
			}

			// 2. REENVIAR COM CONFIRMAÇÃO (mesma Idempotency-Key!)
			const headers = { "Idempotency-Key": idempotencyKey } as Record<
				string,
				string
			>;
			const confirmPayload = { ...payload, confirmar_update: true };

			terminalLog.debug("API_SAVE:REQUEST_CONFIRM", {
				url: `${API_URL}/cdr/arquivos/salvar`,
				method: "POST",
				payload: { ...confirmPayload, caminho_arquivo: "***" },
				headers,
			});

			const data: any = await api("/cdr/arquivos/salvar", {
				method: "POST",
				headers,
				body: JSON.stringify(confirmPayload),
			});

			// 3. VALIDAR RESPOSTA
			if (data?.ok === true || data?.success === true) {
				return true;
			}

			sendToTerminal("❌ [SAVE:API] Erro ao atualizar arquivo", {
				error:
					data?.error?.message ||
					data?.message ||
					"Erro desconhecido na segunda tentativa",
				status: data?.status,
			});
			setErrorMessage(
				`Erro ao atualizar arquivo: ${
					data?.error?.message ||
					data?.message ||
					"Erro desconhecido na segunda tentativa"
				}`,
			);
			return false;
		} catch (e2: any) {
			sendToTerminal("❌ [SAVE:API] Exceção ao confirmar atualização", {
				kind: e2 instanceof APIError ? "APIError" : typeof e2,
				status: e2?.status,
				message: e2?.message,
			});

			if (e2 instanceof APIError) {
				const appError = handleApiError(e2);
				setErrorMessage(appError.message);
				return false;
			}

			setErrorMessage(e2?.message || "Erro ao reenviar confirmação");
			return false;
		}
	}

	/**
	 * CONSOLIDAÇÃO CRÍTICA: Tratamento de conflito 409 para TEMP
	 *
	 * Elimina duplicação das linhas 1329-1386 do original
	 *
	 * Fluxo:
	 * 1. Mostrar diálogo de confirmação
	 * 2. Se confirmado: reconstruir payload e reenviar com confirmar: true
	 * 3. Manter mesma X-Idempotency-Key
	 *
	 * @param options - Opções originais de envio temp
	 * @param idempotencyKey - Idempotency key a manter
	 * @param setErrorMessage - Função de erro
	 * @param showConfirmDialog - Função de diálogo
	 * @returns true se sucesso, false se erro
	 */
	private static async handleConflict409Temp(
		options: TempSaveOptions,
		idempotencyKey: string,
		setErrorMessage: (msg: string) => void,
		showConfirmDialog: () => Promise<boolean>,
	): Promise<boolean> {
		try {
			const { saveData, saveDataRef, savedFilePathRef, lastSaveMetaRef, role } =
				options;

			// 1. MOSTRAR DIÁLOGO
			const confirmar = await showConfirmDialog();
			if (!confirmar) {
				setErrorMessage(ERROR_OPERATION_CANCELLED);
				return false;
			}

			// 2. RECONSTRUIR PAYLOAD (mesma lógica de sendToTemp)
			let working = saveDataRef.current || saveData || {};
			try {
				working = ensureCaminhoArquivo(working);
			} catch {
				// Continuar mesmo se falhar
			}

			const caminho = String(
				savedFilePathRef.current || working?.caminho_arquivo || "",
			);
			const nome_arquivo = caminho
				? caminho.split("\\").pop() || ""
				: working?.nome_arquivo || "";

			let diretorio = "";
			if (caminho && caminho.includes("\\")) {
				diretorio = caminho.substring(0, caminho.lastIndexOf("\\"));
			}
			if (diretorio && !diretorio.endsWith("\\")) {
				diretorio = `${diretorio}\\`;
			}

			const dirNoSlash = diretorio.replace(/\\$/, "");
			const categoria_dir = dirNoSlash
				? dirNoSlash.split("\\").pop() || ""
				: "";
			const entrega = String(
				working?.configuracoes?.entrega || "HOJE",
			).toUpperCase();
			const usuario = String(working?.usuario || role || "usuario");
			const pedido_id = Number(working?.pedido?.orc_id || 0);
			const item_id = Number(working?.pedido?.oit_id || 0);
			const objetos = Array.isArray(working?.objetos)
				? working.objetos.length
				: 0;

			const integridade =
				lastSaveMetaRef.current && lastSaveMetaRef.current.integridade
					? {
							objetosVerificados:
								lastSaveMetaRef.current.integridade.objetosVerificados ?? 0,
							metadadosOk: lastSaveMetaRef.current.integridade.metadadosOk ?? 0,
							metadadosPerdidos:
								lastSaveMetaRef.current.integridade.metadadosPerdidos ?? 0,
						}
					: undefined;

			const request_id = (() => {
				try {
					return (
						readWithAliases<string>(
							Keys.export.lastSaveRequestId,
							["lastSaveRequestId"],
							{ area: "session", persistIfAliased: true },
						) || undefined
					);
				} catch {
					return undefined;
				}
			})();

			// 3. REENVIAR COM CONFIRMAÇÃO (mesma X-Idempotency-Key!)
			const headers = { "X-Idempotency-Key": idempotencyKey } as Record<
				string,
				string
			>;
			const bodyConfirm = {
				fluxo: "temp",
				pedido_id,
				item_id,
				caminho,
				diretorio,
				nome_arquivo,
				categoria_dir,
				produto: working?.produto?.nome || categoria_dir || undefined,
				entrega,
				usuario,
				objetos,
				integridade,
				request_id,
				idempotency_key: idempotencyKey || undefined,
				confirmar: true, // Diferença crítica: temp usa "confirmar", produção usa "confirmar_update"
			};

			sendToTerminal("SAVE_TEMP:API_REQUEST_CONFIRM", {
				headers,
				preview: { ...bodyConfirm, caminho: "***", diretorio: "***" },
			});

			const data: any = await api("/cdr/orcamentos-temp/salvamento", {
				method: "POST",
				headers,
				body: JSON.stringify(bodyConfirm),
			});

			// 4. VALIDAR RESPOSTA
			const ok = !!(
				data?.success === true ||
				data?.ok === true ||
				data?.data?.id
			);

			if (!ok) {
				sendToTerminal("SAVE_TEMP:API_RESPONSE_NOT_SUCCESS_CONFIRM", { data });
				setErrorMessage(
					`Erro ao confirmar Save Temp: ${
						data?.error?.message || data?.message || "Erro desconhecido"
					}`,
				);
				return false;
			}

			sendToTerminal("SAVE_TEMP:API_SUCCESS_CONFIRM", {
				id: data?.data?.id,
				created_at: data?.data?.created_at,
			});

			return true;
		} catch (e2: any) {
			sendToTerminal("SAVE_TEMP:API_CONFIRM_ERROR", {
				kind: e2 instanceof APIError ? "APIError" : typeof e2,
				status: e2?.status,
				message: e2?.message,
				data: e2?.data,
			});

			if (e2 instanceof APIError) {
				const appError = handleApiError(e2);
				setErrorMessage(appError.message);
				return false;
			}

			setErrorMessage(e2?.message || "Erro ao reenviar confirmação temp");
			return false;
		}
	}
}
